import type { InputMode, Reward, StatKey } from '../types';

export interface DisciplineConfig {
  id: string;
  label: string;
  /** Athlete stat that weighs most on performance. */
  primaryStat: StatKey;
  inputMode: InputMode;
  /** Scene key registered with Phaser. */
  sceneKey: string;
  /** Maps a final score to a reward (coins + medal tier). */
  scoreToReward(score: number): Reward;
}

/** Maps a finishing rank (1-based) + score to a reward. */
function rankReward(score: number): Reward {
  if (score >= 900) return { coins: 120, medal: 'gold' };
  if (score >= 750) return { coins: 80, medal: 'silver' };
  if (score >= 600) return { coins: 50, medal: 'bronze' };
  return { coins: 20, medal: 'none' };
}

export const DISCIPLINES: Record<string, DisciplineConfig> = {
  sprint_100m: {
    id: 'sprint_100m',
    label: '100m Sprint',
    primaryStat: 'speed',
    inputMode: 'mash',
    sceneKey: 'Sprint100m',
    scoreToReward: rankReward,
  },
  long_jump: {
    id: 'long_jump',
    label: 'Salto in Lungo',
    primaryStat: 'power',
    inputMode: 'mash+timing',
    sceneKey: 'LongJump',
    scoreToReward: rankReward,
  },
  hurdles: {
    id: 'hurdles',
    label: '110m Ostacoli',
    primaryStat: 'agility',
    inputMode: 'mash+timing',
    sceneKey: 'Hurdles',
    scoreToReward: rankReward,
  },
  javelin: {
    id: 'javelin',
    label: 'Giavellotto',
    primaryStat: 'power',
    inputMode: 'charge+release',
    sceneKey: 'Javelin',
    scoreToReward: rankReward,
  },
  high_jump: {
    id: 'high_jump',
    label: 'Salto in Alto',
    primaryStat: 'agility',
    inputMode: 'mash+timing',
    sceneKey: 'HighJump',
    scoreToReward: rankReward,
  },
  triple_jump: {
    id: 'triple_jump',
    label: 'Salto Triplo',
    primaryStat: 'agility',
    inputMode: 'mash+timing',
    sceneKey: 'TripleJump',
    scoreToReward: rankReward,
  },
  shot_put: {
    id: 'shot_put',
    label: 'Getto del Peso',
    primaryStat: 'power',
    inputMode: 'charge+release',
    sceneKey: 'ShotPut',
    scoreToReward: rankReward,
  },
  hammer_throw: {
    id: 'hammer_throw',
    label: 'Lancio del Martello',
    primaryStat: 'power',
    inputMode: 'charge+release',
    sceneKey: 'HammerThrow',
    scoreToReward: rankReward,
  },
  pole_vault: {
    id: 'pole_vault',
    label: "Salto con l'Asta",
    primaryStat: 'agility',
    inputMode: 'mash+timing',
    sceneKey: 'PoleVault',
    scoreToReward: rankReward,
  },
  discus: {
    id: 'discus',
    label: 'Lancio del Disco',
    primaryStat: 'power',
    inputMode: 'charge+release',
    sceneKey: 'DiscusThrow',
    scoreToReward: rankReward,
  },
  weightlifting: {
    id: 'weightlifting',
    label: 'Sollevamento Pesi',
    primaryStat: 'power',
    inputMode: 'charge+release',
    sceneKey: 'Weightlifting',
    scoreToReward: rankReward,
  },
  swim_50m: {
    id: 'swim_50m',
    label: '50m Nuoto',
    primaryStat: 'speed',
    inputMode: 'mash',
    sceneKey: 'Swim50m',
    scoreToReward: rankReward,
  },
  diving: {
    id: 'diving',
    label: 'Tuffi',
    primaryStat: 'agility',
    inputMode: 'mash+timing',
    sceneKey: 'Diving',
    scoreToReward: rankReward,
  },
  archery: {
    id: 'archery',
    label: "Tiro con l'Arco",
    primaryStat: 'technique',
    inputMode: 'charge+release',
    sceneKey: 'Archery',
    scoreToReward: rankReward,
  },
};

/** Coarse grouping used by the discipline-picker filter badges. */
export type DisciplineCategory = 'run' | 'jump' | 'throw' | 'strength' | 'water' | 'precision';
export const DISCIPLINE_CATEGORY: Record<string, DisciplineCategory> = {
  sprint_100m: 'run', hurdles: 'run',
  long_jump: 'jump', high_jump: 'jump', triple_jump: 'jump', pole_vault: 'jump',
  javelin: 'throw', shot_put: 'throw', hammer_throw: 'throw', discus: 'throw',
  weightlifting: 'strength',
  swim_50m: 'water', diving: 'water',
  archery: 'precision',
};

export function getDiscipline(id: string): DisciplineConfig {
  const d = DISCIPLINES[id];
  if (!d) throw new Error(`Unknown discipline "${id}"`);
  return d;
}
